import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { portalCreatePayLink, portalFetchInvoices } from "../../services/portalApi";
import PortalShell from "./PortalShell";
import "./portal.css";

const TABS = [
  { id: "all", label: "All" },
  { id: "unpaid", label: "Unpaid" },
  { id: "paid", label: "Paid" },
];

const formatMoney = (n) =>
  `₹${(parseFloat(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (d) => {
  if (!d || d === "0000-00-00") return "—";
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

function statusBadge(inv) {
  if (!inv.payable) return { cls: "portal-badge--paid", label: "Paid" };
  const due = inv.due_date ? new Date(`${inv.due_date}T12:00:00`) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (due && due < today) return { cls: "portal-badge--overdue", label: "Overdue" };
  return { cls: "portal-badge--due", label: "Due" };
}

export default function PortalInvoices() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  const load = async (filter) => {
    setLoading(true);
    try {
      const rows = await portalFetchInvoices(filter);
      setInvoices(rows);
    } catch (err) {
      toast.error(err.message || "Could not load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(tab);
  }, [tab]);

  const handlePay = async (invoice) => {
    setPayingId(invoice.id);
    try {
      const data = await portalCreatePayLink(invoice.id);
      if (data.token) {
        navigate(`/pay/invoice/${data.token}`);
        return;
      }
      if (data.pay_url) {
        window.location.href = data.pay_url;
        return;
      }
      toast.error("Pay link unavailable");
    } catch (err) {
      toast.error(err.message || "Could not start payment");
    } finally {
      setPayingId(null);
    }
  };

  return (
    <PortalShell
      title="My invoices"
      subtitle="Published invoices for your account. Pay unpaid balances securely online."
    >
      <div className="portal-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`portal-tab ${tab === t.id ? "portal-tab--active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="portal-empty">Loading invoices…</p>
      ) : invoices.length === 0 ? (
        <div className="portal-empty">No invoices in this view.</div>
      ) : (
        invoices.map((inv) => {
          const badge = statusBadge(inv);
          return (
            <article key={inv.id} className="portal-card">
              <div className="portal-card__row">
                <div>
                  <h2 className="portal-card__title">{inv.invoice_number}</h2>
                  <p className="portal-card__meta">
                    {formatDate(inv.invoice_date)}
                    {inv.due_date ? ` · Due ${formatDate(inv.due_date)}` : ""}
                  </p>
                </div>
                <span className={`portal-badge ${badge.cls}`}>{badge.label}</span>
              </div>
              <div className="portal-card__row" style={{ marginTop: "0.75rem" }}>
                <div>
                  <p className="portal-card__meta">
                    Total{" "}
                    <span className="portal-amount">{formatMoney(inv.grand_total)}</span>
                  </p>
                  {inv.payable ? (
                    <p className="portal-card__meta">
                      Balance due{" "}
                      <span className="portal-amount portal-amount--due">
                        {formatMoney(inv.pending)}
                      </span>
                    </p>
                  ) : (
                    <p className="portal-card__meta">
                      Paid in full
                    </p>
                  )}
                  {parseFloat(inv.collected) > 0 && inv.payable ? (
                    <p className="portal-card__meta">
                      Paid so far{" "}
                      <span className="portal-amount">{formatMoney(inv.collected)}</span>
                    </p>
                  ) : null}
                </div>
                {inv.payable ? (
                  <button
                    type="button"
                    className="portal-btn portal-btn--primary"
                    disabled={payingId === inv.id}
                    onClick={() => handlePay(inv)}
                  >
                    {payingId === inv.id ? "Loading…" : "Pay now"}
                  </button>
                ) : null}
              </div>
            </article>
          );
        })
      )}
    </PortalShell>
  );
}
